import {
  createSystem,
  defaultConfig,
  defineAnimationStyles,
  defineConfig,
  defineRecipe,
  defineSlotRecipe,
} from '@chakra-ui/react';

import { buttonRecipe } from 'shared/components/Button';

import { alertRecipe } from './components/Alert/recipe';

const checkboxSlotRecipe = defineSlotRecipe({
  className: 'chakra-checkbox',
  slots: ['control', 'label', 'root', 'indicator'],
  base: {
    root: {
      display: 'inline-flex',
      gap: '2',
      alignItems: 'center',
      verticalAlign: 'top',
      position: 'relative',
    },
    label: {
      fontWeight: 'medium',
      userSelect: 'none',
      _disabled: {
        opacity: '0.5',
      },
    },
    indicator: {
      color: 'fgPrimaryInverse',
    },
  },
  variants: {
    visual: {
      outline: {
        control: {
          _checked: {
            bg: 'bgCompAccent',
            borderColor: 'brdAccent',
            _hover: {
              bg: 'bgCompAccent',
              borderColor: 'brdAccent',
            },
          },
          _focusVisible: {
            boxShadow: 'none',
          },
          borderColor: 'brdAccent',
          borderWidth: '1px',
          borderRadius: 'sm',
        },
      },
      primary: {
        control: {
          borderColor: 'fgPrimary',
          borderWidth: '1px',
          borderRadius: 'sm',
          bg: 'transparent',
          _checked: {
            bg: 'fgPrimary',
            borderColor: 'fgPrimary',
            _hover: {
              bg: 'fgPrimary',
              borderColor: 'fgPrimary',
            },
          },
          _focusVisible: {
            boxShadow: 'none',
          },
        },
        indicator: {
          color: 'fgPrimaryInverse',
        },
      },
    },
    shape: {
      rounded: {
        control: { borderRadius: 'full' },
      },
    },
    size: {
      sm: {
        label: {
          fontSize: 'xs',
        },
      },
      md: {
        label: {
          fontSize: 'sm',
        },
      },
    },
  },
  defaultVariants: {
    visual: 'outline',
  },
});

const dialogSlotRecipe = defineSlotRecipe({
  slots: ['content'],
  base: {
    content: {
      bg: 'bgPrimary',
      borderRadius: 'borderRadius03',
      maxH: '65vh',
    },
  },
  variants: {
    size: {
      sm: {
        content: {
          maxW: '30rem',
        },
      },
      md: {
        content: {
          maxW: '39.875rem',
        },
      },
      lg: {
        content: {
          maxW: '46rem',
        },
      },
      auto: {
        content: {
          width: 'auto',
        },
      },
    },
  },
});

const drawerSlotRecipe = defineSlotRecipe({
  slots: ['content'],
  base: {
    content: {
      bg: 'bgPrimary',
    },
  },
  variants: {
    size: {
      xs: {
        content: {
          maxWidth: '384px',
        },
      },
    },
  },
  defaultVariants: {
    size: 'xs',
  },
});

const menuSlotRecipe = defineSlotRecipe({
  slots: ['content', 'item', 'button', 'itemGroupLabel'],
  base: {
    content: {
      bg: 'bgPrimary',
      fontSize: 'small',
      minWidth: 'auto',
      maxWidth: '300px',
      p: 0,
      overflowX: 'hidden',
      _focusVisible: {
        outline: 'none',
      },
    },
    item: {
      bg: 'bgPrimary',
      _hover: { bg: 'bgSecondary' },
      _focusVisible: { bg: 'bgSecondary' },
      h: '32px',
      whiteSpace: 'nowrap',
      color: 'fgPrimary',
    },
    button: {
      borderRadius: 'xs',
      _active: { bg: 'bgSecondary' },
      _focusVisible: {
        outline: 'none',
      },
    },
    itemGroupLabel: {
      fontWeight: '600',
      fontSize: '12px',
      color: 'fgPrimary',
    },
  },
});

const popoverSlotRecipe = defineSlotRecipe({
  className: 'chakra-popover',
  slots: ['content'],
  base: {
    content: {
      width: 'auto',
      _focusVisible: {
        boxShadow: 'none',
      },
    },
  },
});

const radioGroupSlotRecipe = defineSlotRecipe({
  className: 'chakra-radio-group',
  slots: ['itemControl'],
  base: {
    itemControl: {
      _focusVisible: {
        boxShadow: 'none',
      },
    },
  },
  variants: {
    colorPalette: {
      blue: {
        itemControl: {
          _checked: {
            bg: 'bgCompAccent',
            borderColor: 'brdAccent',
          },
        },
      },
    },
  },
  defaultVariants: {
    colorPalette: 'blue',
  },
});

const switchSlotRecipe = defineSlotRecipe({
  className: 'chakra-switch',
  slots: ['root', 'control', 'thumb'],
  base: {
    root: {
      display: 'inline-flex',
      gap: '2.5',
      alignItems: 'center',
      position: 'relative',
      verticalAlign: 'middle',
      '--switch-diff': 'calc(var(--switch-width) - var(--switch-height))',
      '--switch-x': {
        base: 'var(--switch-diff)',
        _rtl: 'calc(var(--switch-diff) * -1)',
      },
    },
    control: {
      display: 'inline-flex',
      gap: '0.5rem',
      flexShrink: 0,
      justifyContent: 'flex-start',
      cursor: 'switch',
      borderRadius: 'full',
      position: 'relative',
      width: 'var(--switch-width)',
      height: 'var(--switch-height)',
      transition: 'backgrounds',
      _disabled: {
        opacity: '0.5',
        cursor: 'not-allowed',
      },
      _invalid: {
        outline: '2px solid',
        outlineColor: 'border.error',
        outlineOffset: '2px',
      },
      _focusVisible: {
        boxShadow: 'none',
      },
    },

    thumb: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      transitionProperty: 'translate',
      transitionDuration: 'fast',
      borderRadius: 'inherit',
      _checked: {
        translate: 'var(--switch-x) 0',
      },
    },
  },
  variants: {
    variant: {
      solid: {
        control: {
          borderRadius: 'full',
          bg: 'bgCompSecondaryTiny',
          focusVisibleRing: 'outside',
          _checked: {
            bg: 'bgCompAccent',
          },
        },
        thumb: {
          bg: 'white',
          width: 'var(--switch-height)',
          height: 'var(--switch-height)',
          scale: '0.8',
          boxShadow: 'sm',
          _checked: {
            bg: '#ffffff',
          },
        },
      },
    },
  },
  defaultVariants: {
    variant: 'solid',
  },
});

const sliderSlotRecipe = defineSlotRecipe({
  className: 'chakra-slider',
  slots: ['root', 'control', 'thumb', 'track'],
  variants: {
    variant: {
      outline: {
        thumb: {
          bg: 'black',
        },
      },
    },
  },
  defaultVariants: {
    variant: 'outline',
  },
});

const inputRecipe = defineRecipe({
  variants: {
    variant: {
      outline: {
        borderColor: 'brdSecondary',
        borderRadius: 'lg',
        color: 'fgPrimary',
        _disabled: {
          bg: 'bgCompSecondaryMid',
        },
        _invalid: {
          borderColor: 'brdDanger',
        },
        _placeholder: {
          color: 'fgSecondary',
        },
        _focus: {
          outline: 'none',
        },
        _focusVisible: {
          borderColor: 'fgPrimary',
          boxShadow: 'none',
          _invalid: {
            borderColor: 'brdDanger',
          },
        },
      },
    },
  },
});

const textareaRecipe = defineRecipe({
  base: {
    _invalid: {
      borderColor: 'brdDanger',
    },
    _focusVisible: {
      _invalid: {
        borderColor: 'brdDanger',
      },
    },
  },
  variants: {
    visual: {
      outline: {
        borderColor: 'brdSecondary',
        borderRadius: 'lg',
        color: 'fgPrimary',
        _disabled: {
          bg: 'bgCompSecondaryMid',
        },
        _placeholder: {
          color: 'fgSecondary',
        },
        _focusVisible: {
          borderColor: 'fgPrimary',
          boxShadow: 'none',
        },
      },
    },
  },
  defaultVariants: {
    visual: 'outline',
  },
});

const badgeRecipe = defineRecipe({
  base: {
    borderRadius: 'xl',
  },
  variants: {
    colorPalette: {
      lightOrange: { bg: 'bgCompMinorWarning', _dark: { color: 'white' } },
      lightBlue: { bg: 'bgCompMinorAccent', _dark: { color: 'white' } },
      lightYellow: { bg: 'bgCompMinorWarning', _dark: { color: 'white' } },
      lightRed: { bg: 'bgCompMinorDanger', _dark: { color: 'white' } },
      lightGray: { bg: 'bgCompSecondaryTiny', _dark: { color: 'white' } },
      lightGreen: { bg: 'bgCompMinorSuccess', _dark: { color: 'white' } },
      lightPurple: { bg: 'purple.200' },
      blue: { bg: 'bgCompAccent' },
      gray: { bg: 'bgCompSecondaryMid', _dark: { color: 'white' } },
      darkGray: { bg: 'bgCompPrimaryInverse', color: 'fgPrimaryInverse' },
      green: { bg: 'bgCompSuccess' },
      teal: { bg: 'teal' },
      orange: { bg: 'bgCompWarning' },
      red: { bg: 'bgCompDanger' },
      black: { bg: 'black', color: 'white' },
      white: { bg: 'bgPrimary', _dark: { color: 'white' } },
      whiteTotal: { bg: 'white' },
    },
  },
  defaultVariants: {
    colorPalette: 'gray',
  },
});

const animationStyles = defineAnimationStyles({
  fadeIn: {
    value: {
      animationName: 'fade-in',
      animationDuration: '500ms',
      animationTimingFunction: 'ease-in',
    },
  },
  fadeOut: {
    value: {
      animationName: 'fade-out',
      animationDuration: '500ms',
      animationTimingFunction: 'ease-in',
    },
  },
});

const config = defineConfig({
  theme: {
    slotRecipes: {
      menu: menuSlotRecipe,
      popover: popoverSlotRecipe,
      radioGroup: radioGroupSlotRecipe,
      switch: switchSlotRecipe,
      slider: sliderSlotRecipe,
      checkbox: checkboxSlotRecipe,
      dialog: dialogSlotRecipe,
      drawer: drawerSlotRecipe,
      alert: alertRecipe,
    },
    keyframes: {
      'fade-in': {
        '0%': { opacity: 0 },
        '100%': { opacity: 1 },
      },
      'fade-out': {
        '0%': { opacity: 1 },
        '100%': { opacity: 0 },
      },
      'morpheus-glow': {
        '0%, 100%': {
          boxShadow:
            '0 0 5px rgba(138, 43, 226, 0.5), 0 0 10px rgba(138, 43, 226, 0.3)',
        },
        '50%': {
          boxShadow:
            '0 0 20px rgba(138, 43, 226, 0.8), 0 0 30px rgba(138, 43, 226, 0.5)',
        },
      },
    },
    animationStyles,
    tokens: {
      animations: {
        fadeIn: { value: 'fade-in 500ms ease-in' },
        fadeOut: { value: 'fade-out 500ms ease-in' },
      },
      fontSizes: {
        xsm: { value: '0.625rem' },
        small: { value: '0.75rem' },
        meta: { value: '0.875rem' },
        body: { value: '1rem' },
        smallHeading: { value: '1.125rem' },
        heading: { value: '1.25rem' },
        semiLargeHeading: { value: '1.5rem' },
        largeHeading: { value: '2rem' },
        extraLargeHeading: { value: '2.5rem' },
      },
    },
    semanticTokens: {
      colors: {
        bgPrimary: {
          value: {
            base: '#ffffff',
            _dark: '#17181c',
          },
        },
        bgSecondary: {
          value: {
            base: '#f2f2f7',
            _dark: '#000000',
          },
        },
        bgTertiary: {
          value: {
            base: '#f8f9fd',
            _dark: '#202227',
          },
        },
        bgCompPrimary: {
          value: {
            base: '#ffffff',
            _dark: '#3a4053',
          },
        },
        bgCompPrimaryInverse: {
          value: {
            base: '#17181c',
            _dark: '#ffffff',
          },
        },
        bgCompSecondaryTiny: {
          value: {
            base: '#66739933',
            _dark: '#73798c66',
          },
        },
        bgCompSecondaryMid: {
          value: {
            base: '#6673991f',
            _dark: '#73798c3d',
          },
        },
        bgCompSecondaryBig: {
          value: {
            base: '#6673990f',
            _dark: '#66739940',
          },
        },
        bgStripedRow: {
          value: {
            base: '#66739908',
            _dark: '#73798c10',
          },
        },
        bgCompAccent: {
          value: {
            base: '#3f7bf3',
            _dark: '#2267f1',
          },
        },
        bgCompMinorAccent: {
          value: {
            base: '#d9e5fd',
            _dark: '#062056',
          },
        },
        bgCompHoverAccent: {
          value: {
            base: '#326de3',
            _dark: '#1e5cd9',
          },
        },
        bgCompSuccess: {
          value: {
            base: '#39ac6f',
            _dark: '#349b64',
          },
        },
        bgCompMinorSuccess: {
          value: {
            base: '#d1f0df',
            _dark: '#1e3428',
          },
        },
        bgCompWarning: {
          value: {
            base: '#eeba2a',
            _dark: '#e9b013',
          },
        },
        bgCompMinorWarning: {
          value: {
            base: '#faeabf',
            _dark: '#4e3b06',
          },
        },
        bgCompDanger: {
          value: {
            base: '#ee4946',
            _dark: '#eb2c28',
          },
        },
        bgCompMinorDanger: {
          value: {
            base: '#fcdada',
            _dark: '#3b2626',
          },
        },
        bgTasmManagerOutlined: {
          value: {
            base: 'var(--chakra-colors-blue-50)',
            _dark: '#27272a',
          },
        },
        fgPrimary: {
          value: {
            base: '#000000',
            _dark: '#ffffff',
          },
        },
        fgPrimaryInverse: {
          value: {
            base: '#ffffff',
            _dark: '#000000',
          },
        },
        fgSecondary: {
          value: {
            base: '#393d4699',
            _dark: '#ffffff99',
          },
        },
        fgTertiary: {
          value: {
            base: '#393d464d',
            _dark: '#ffffff4d',
          },
        },
        fgInteractive: {
          value: {
            base: '#0f56e6',
            _dark: '#5288f4',
          },
        },
        fgSuccess: {
          value: {
            base: '#2e8a59',
            _dark: '#42c07d',
          },
        },
        fgWarning: {
          value: {
            base: '#d09d11',
            _dark: '#f0c140',
          },
        },
        fgDanger: {
          value: {
            base: '#e01815',
            _dark: '#ef5a57',
          },
        },
        lifeDimsCareer: {
          value: {
            base: '#129ade',
            _dark: '#129ade',
          },
        },
        lifeDimsMinorCareer: {
          value: {
            base: '#cdebfa',
            _dark: '#08354d',
          },
        },
        lifeDimsEnjoyment: {
          value: {
            base: '#ea8c2e',
            _dark: '#ea8c2e',
          },
        },
        lifeDimsMinorEnjoyment: {
          value: {
            base: '#fae3cd',
            _dark: '#4a2d10',
          },
        },
        lifeDimsHealth: {
          value: {
            base: '#eb4770',
            _dark: '#eb4770',
          },
        },
        lifeDimsMinorHealth: {
          value: {
            base: '#facdd8',
            _dark: '#4d1724',
          },
        },
        lifeDimsRelationships: {
          value: {
            base: '#7950f1',
            _dark: '#7950f1',
          },
        },
        lifeDimsMinorRelationships: {
          value: {
            base: '#d8cdfa',
            _dark: '#2a1d52',
          },
        },
        lifeDimsFinances: {
          value: {
            base: '#2caf9a',
            _dark: '#2caf9a',
          },
        },
        lifeDimsMinorFinances: {
          value: {
            base: '#cdfaf3',
            _dark: '#11453d',
          },
        },
        lifeDimsImpact: {
          value: {
            base: '#cf47eb',
            _dark: '#cf47eb',
          },
        },
        lifeDimsMinorImpact: {
          value: {
            base: '#f2cdfa',
            _dark: '#43184d',
          },
        },
        lifeDimsSpirituality: {
          value: {
            base: '#eeba2a',
            _dark: '#eeba2a',
          },
        },
        lifeDimsMinorSpirituality: {
          value: {
            base: '#faeecd',
            _dark: '#4a3a0f',
          },
        },
        divPrimary: {
          value: {
            base: '#6673991f',
            _dark: '#73798c3d',
          },
        },
        brdPrimary: {
          value: {
            base: '#17181c',
            _dark: '#ffffff',
          },
        },
        brdSecondary: {
          value: {
            base: '#66739933',
            _dark: '#73798c66',
          },
        },
        brdAccent: {
          value: {
            base: '#3f7bf3',
            _dark: '#3f7bf3',
          },
        },
        brdSuccess: {
          value: {
            base: '#39ac6f',
            _dark: '#39ac6f',
          },
        },
        brdDanger: {
          value: {
            base: '#eb2c28',
            _dark: '#eb2c28',
          },
        },
        ovlPrimary: {
          value: {
            base: '#17181c66',
            _dark: '#17181c66',
          },
        },
      },
    },
    recipes: {
      badge: badgeRecipe,
      button: buttonRecipe,
      input: inputRecipe,
      textarea: textareaRecipe,
    },
  },
  globalCss: {
    body: {
      bg: 'bgPrimary',
    },
  },
});

export const system = createSystem(defaultConfig, config);
